/**
 * @file api-backups.ts
 * @description API-клиент платформенных бэкапов (phase08.9 post-smoke;
 * phase19.26 — база, реальные бакеты, e-mail о сбое, dead-man; шаг 7.1 —
 * ключ агента только маской, экстренный отзыв, папка копий при установке,
 * внешние хранилища под флагом, итог запуска `summary`).
 *
 * Зеркало `apps/api/src/backups/**` и docs/api/platform-backups.md. Здесь же
 * — реестр бакетов и этапов запуска с русскими подписями и чистые правила
 * отображения («зависший» запуск, проверка e-mail): их делят страница и тесты.
 */

import { ApiError, apiFetch } from "./api-client";

export interface BackupConfigStats {
  runsLast30Days: number;
  successLast30Days: number;
  failuresLast30Days: number;
}

/**
 * Куда складывать копии (phase19.26 шаг 7): диск сервера приложения или
 * внешнее хранилище, куда агент синхронизирует копию через rclone.
 */
export type BackupDestinationType = "LOCAL" | "SMB" | "SFTP" | "S3" | "WEBDAV";

/**
 * Учётные данные назначения в PATCH (заменяются целиком); в ответе их нет —
 * только `hasCredentials` и маска логина `credentialsLogin`.
 */
export type BackupDestinationCredentials =
  // SMB: пароль необязателен (гостевая общая папка).
  | { user: string; password?: string }
  | { user: string; privateKey: string }
  | { accessKey: string; secretKey: string; region?: string };

/**
 * Диск локальной копии (phase19.26, решение пользователя 2026-09-26):
 * агент присылает замер после запуска. Байты — строками (BigInt).
 */
export interface BackupDiskStats {
  path: string;
  totalBytes: string;
  freeBytes: string;
  usedBytes: string;
  /** Сколько занимают сами копии. */
  backupBytes: string;
  hostname: string | null;
  /** Когда агент прислал замер; null — неизвестно. */
  updatedAt: string | null;
}

export interface BackupConfigDto {
  id: string;
  scope: string;
  isEnabled: boolean;
  cronExpression: string;
  /** phase19.26 — дамп PostgreSQL в составе бэкапа. */
  databaseIncluded: boolean;
  bucketsIncluded: string[];
  retentionDays: number;
  /** phase19.26 шаг 7 — тип назначения копий. */
  destinationType: BackupDestinationType;
  /** Адрес/хост/endpoint внешнего хранилища; у LOCAL — null. */
  destinationUrl: string | null;
  /**
   * Путь/бакет/папка во внешнем хранилище. Для LOCAL панель его не меняет
   * (шаг 7.1, Р1): папка локальных копий задаётся при установке — см. `localRoot`.
   */
  destinationPath: string;
  /** Папка локальных копий по отчёту агента; null — агент ещё не отчитывался. */
  localRoot: string | null;
  /**
   * Можно ли выбрать внешнее хранилище (шаг 7.1, Р4): пока копии не шифруются —
   * false, PATCH с типом ≠ LOCAL отвечает 422 `backup_destination_external_disabled`.
   */
  externalDestinationsEnabled: boolean;
  /** Оставлять ли копию на диске сервера при внешнем хранилище. */
  keepLocalCopy: boolean;
  /** Учётка назначения сохранена (секреты панели не отдаются). */
  hasCredentials: boolean;
  /** Маска логина / accessKey: «bac***»; null — учётки нет. */
  credentialsLogin: string | null;
  /** Когда запрошена проверка подключения; null — не запрашивалась. */
  verifyDestinationRequestedAt: string | null;
  /** Диск локальной копии; null — замера ещё не было (до первого бэкапа). */
  diskStats: BackupDiskStats | null;
  /** Места на диске копий мало: свободно < 10 % или < 5 ГиБ. */
  diskWarning: boolean;
  /** phase19.26 — письма о сбое; null — не слать. */
  alertEmail: string | null;
  lastSuccessAt: string | null;
  /** phase19.26 dead-man: успеха нет или он старше 36 ч. */
  stale: boolean;
  lastRunStatus: string | null;
  lastErrorMessage: string | null;
  updatedAt: string;
  stats: BackupConfigStats;
}

export interface BackupRunDto {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: "RUNNING" | "SUCCESS" | "FAILED";
  /**
   * Этап: `postgres`, `verify`, `offsite` или имя бакета; `summary` — итог
   * запуска целиком (шаг 7.1, Р5: успех, только если удались все этапы).
   */
  bucket: string | null;
  bytesCopied: string | null;
  objectsCount: number | null;
  durationMs: number | null;
  errorMessage: string | null;
  triggeredBy: string;
  createdAt: string;
}

export interface UpdateBackupConfigPayload {
  isEnabled?: boolean;
  cronExpression?: string;
  databaseIncluded?: boolean;
  bucketsIncluded?: string[];
  retentionDays?: number;
  destinationType?: BackupDestinationType;
  destinationUrl?: string | null;
  /** Для LOCAL не отправляется — сервер его игнорирует (папка задаётся при установке). */
  destinationPath?: string;
  keepLocalCopy?: boolean;
  /** `undefined` — оставить как есть, `null` — стереть (LOCAL). */
  destinationCredentials?: BackupDestinationCredentials | null;
  /** Пустая строка/null — не слать письма (сервер приводит "" к null). */
  alertEmail?: string | null;
}

export async function getBackupConfig(): Promise<BackupConfigDto> {
  return apiFetch("/admin/platform/backups");
}

export async function updateBackupConfig(
  payload: UpdateBackupConfigPayload,
): Promise<BackupConfigDto> {
  return apiFetch("/admin/platform/backups", { method: "PATCH", json: payload });
}

export async function listBackupRuns(params?: {
  limit?: number;
  offset?: number;
  status?: "RUNNING" | "SUCCESS" | "FAILED";
}): Promise<{ items: BackupRunDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  if (params?.status) search.set("status", params.status);
  const qs = search.toString();
  return apiFetch(`/admin/platform/backups/runs${qs ? `?${qs}` : ""}`);
}

export async function triggerManualBackup(): Promise<BackupRunDto> {
  return apiFetch("/admin/platform/backups/trigger", { method: "POST" });
}

/**
 * Сведения о ключе доступа агента (webhook). Полный ключ панель не получает
 * нигде (шаг 7.1, Р3): агенту его передаёт команда установки или `agent-config`.
 */
export interface BackupWebhookInfoDto {
  /**
   * Куда агент шлёт отчёты. null — у сервера не задан публичный адрес API:
   * маска и замена ключа работают, но команду установки получить нельзя.
   */
  url: string | null;
  /** Маска ключа: «••••abcd»; null — ключа ещё нет. */
  tokenMask: string | null;
  createdAt: string | null;
  /** Есть неподтверждённый агентом новый ключ. */
  pending: boolean;
  /** С какого момента новый ключ ждёт агента; null — замены нет. */
  pendingSince: string | null;
  /** Агент не подтвердил новый ключ за 48 ч. */
  pendingStale: boolean;
}

/** Одноразовая команда установки агента (действует час, ключ — внутри команды). */
export interface BackupInstallCommand {
  command: string;
  expiresAt: string;
}

/** Строка `details.errors` ошибки 400 `backup_destination_invalid`. */
export interface BackupFieldError {
  field: string;
  message: string;
}

/** «Проверить подключение»: агент проверит при следующем запуске. */
export async function requestBackupDestinationVerify(): Promise<{ requestedAt: string }> {
  return apiFetch("/admin/platform/backups/destination/verify", { method: "POST" });
}

/**
 * Команда установки агента. `backupRoot` — папка локальных копий на сервере
 * (шаг 7.1, Р1); не передана — сервер берёт значение по умолчанию. Неверная
 * папка — 400.
 */
export async function createBackupInstallCommand(
  backupRoot?: string,
): Promise<BackupInstallCommand> {
  return apiFetch("/admin/platform/backups/install-token", {
    method: "POST",
    json: backupRoot ? { backupRoot } : {},
  });
}

export async function getBackupWebhookInfo(): Promise<BackupWebhookInfoDto> {
  return apiFetch("/admin/platform/backups/webhook-info");
}

/**
 * Плановая замена ключа: ответ — как у `webhook-info` (только маска).
 * `pending: true` — новый ключ ждёт агента; `false` — действующего ключа не
 * было, новый действует сразу.
 */
export async function rotateBackupWebhookToken(): Promise<BackupWebhookInfoDto> {
  return apiFetch("/admin/platform/backups/webhook-info/rotate", {
    method: "POST",
  });
}

/**
 * Экстренный отзыв (шаг 7.1, Р2): старый ключ гаснет сразу, ожидающий
 * сбрасывается. Агент продолжает локальные копии с настройками по умолчанию,
 * но не отчитывается в панель и не читает её настройки, пока на сервере заново
 * не выполнят команду установки. Ответ — как у `webhook-info` (`pending: false`).
 */
export async function revokeBackupWebhookToken(): Promise<BackupWebhookInfoDto> {
  return apiFetch("/admin/platform/backups/webhook-info/revoke", {
    method: "POST",
  });
}

// ─── Реестры подписей ───────────────────────────────────────────────────────

/**
 * Бакеты хранилища, которые копирует скрипт (`ALLOWED_BUCKETS` сервера).
 * `tmp` и `labels` не копируются — они регенерируемые. Новый бакет = запись.
 */
export const BACKUP_BUCKETS: Array<{ id: string; label: string }> = [
  { id: "chaiberi-documents", label: "Документы" },
  { id: "chaiberi-product-media", label: "Медиа товаров" },
  { id: "chaiberi-certificates", label: "Сертификаты" },
  { id: "chaiberi-content-media", label: "Медиа контента" },
  { id: "chaiberi-avatars", label: "Аватары" },
];

/** Этапы запуска, которые не являются бакетами. */
const STAGE_LABEL: Record<string, string> = {
  summary: "Итог запуска",
  postgres: "База данных",
  verify: "Проверка восстановления",
  offsite: "Копия в хранилище",
  "offsite-check": "Проверка хранилища",
};

/** Подпись этапа запуска: «База данных», «Медиа товаров»; неизвестное — как есть. */
export function backupStageLabel(bucket: string | null): string {
  if (!bucket) return "Весь запуск";
  return (
    STAGE_LABEL[bucket] ?? BACKUP_BUCKETS.find((b) => b.id === bucket)?.label ?? bucket
  );
}

/** Строка истории — итог запуска целиком (`summary`), а не отдельный этап. */
export function isRunSummary(run: Pick<BackupRunDto, "bucket">): boolean {
  return run.bucket === "summary";
}

/** Запуск «висит» в RUNNING дольше суток — скрипт не прислал итог. */
export const STUCK_RUN_MS = 24 * 60 * 60 * 1000;

export function isRunStuck(run: BackupRunDto, now: Date = new Date()): boolean {
  if (run.status !== "RUNNING") return false;
  const started = new Date(run.startedAt).getTime();
  return Number.isFinite(started) && now.getTime() - started > STUCK_RUN_MS;
}

/**
 * Проверка e-mail для уведомлений: пусто — допустимо (письма не шлём),
 * иначе простой шаблон «x@y.z» до 254 символов — точную проверку делает
 * сервер (`@IsEmail`). Возвращает текст ошибки или null.
 */
export function alertEmailError(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (value.length > 254) return "Адрес длиннее 254 символов.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Похоже на опечатку — проверьте адрес.";
  return null;
}

/**
 * Полоса здоровья бэкапа над настройками (dead-man, phase19.26):
 * выключено — нейтральная подсказка (тишина ожидаема, это не тревога);
 * включено и `stale` — предупреждение; иначе полосы нет.
 */
export function backupHealthNotice(
  cfg: Pick<BackupConfigDto, "isEnabled" | "stale" | "lastSuccessAt">,
  /** Где показывается: на странице «Бэкапы» (настройки ниже) или на «Обзоре» платформы. */
  where: "backups-page" | "overview" = "backups-page",
): { tone: "info" | "warning"; text: string } | null {
  if (!cfg.isEnabled) {
    return {
      tone: "info",
      text:
        where === "overview"
          ? "Бэкапы выключены — по расписанию ничего не копируется. Включите их в разделе «Бэкапы»."
          : "Бэкапы выключены — по расписанию ничего не копируется. Включите их в настройках ниже.",
    };
  }
  if (!cfg.stale) return null;
  return {
    tone: "warning",
    text: cfg.lastSuccessAt
      ? "Больше 36 ч без успешного бэкапа — проверьте историю запусков и что бэкап на сервере приложения запускается."
      : "Больше 36 ч без успешного бэкапа: успешных запусков ещё не было — проверьте, что бэкап на сервере приложения запускается.",
  };
}

/** Человекочитаемые подписи. */
export const RUN_STATUS_LABEL: Record<BackupRunDto["status"], string> = {
  RUNNING: "Выполняется",
  SUCCESS: "Успешно",
  FAILED: "Ошибка",
};

export const TRIGGER_LABEL: Record<string, string> = {
  schedule: "По расписанию",
  manual: "Вручную",
  api: "API",
};

/**
 * Ошибки назначения из 400 `backup_destination_invalid` (`details.errors`):
 * список «поле — текст» для показа под карточкой; другая ошибка — null.
 */
export function destinationFieldErrors(err: unknown): BackupFieldError[] | null {
  if (!(err instanceof ApiError) || err.code !== "backup_destination_invalid") return null;
  const raw = (err.details as { errors?: unknown } | undefined)?.errors;
  if (!Array.isArray(raw)) return [{ field: "", message: err.message }];
  return raw
    .filter((e): e is BackupFieldError =>
      typeof e === "object" && e !== null && typeof (e as BackupFieldError).message === "string",
    )
    .map((e) => ({ field: typeof e.field === "string" ? e.field : "", message: e.message }));
}

/**
 * Человеческие тексты ошибок API бэкапов по коду — реестром: новый код =
 * запись. Неизвестный код — сообщение сервера, не `ApiError` — `fallback`.
 */
const BACKUP_ERROR_TEXT: Record<string, string> = {
  backup_destination_external_disabled:
    "Внешнее хранилище пока недоступно: оно появится после добавления шифрования копий. Сейчас копии хранятся только на сервере приложения.",
  backup_public_url_invalid:
    "Не удалось собрать команду: у сервера не задан публичный адрес API. Обратитесь к администратору платформы.",
  // В production адрес берётся только из PUBLIC_API_URL (https) — тот же текст для владельца.
  backup_public_api_url_required:
    "Не удалось собрать команду: у сервера не задан публичный адрес API. Обратитесь к администратору платформы.",
};

export function backupErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  return BACKUP_ERROR_TEXT[err.code] ?? (err.message || fallback);
}

/** Размер в байтах — общий форматтер `lib/format.ts` (реэкспорт для старых импортов). */
export { formatBytes } from "./format";

/** Длительность в мс → человекочитаемая строка. */
export function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} мс`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} с`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins} мин ${secs} с`;
}
